import { useState, useCallback } from 'react';
import JSZip from 'jszip';
import { useToast } from '@/hooks/use-toast';

export interface SelectableItem {
  id: string;
  prompt?: string;
  title?: string;
  text?: string;
  // URLs for downloading
  image_url?: string | null;
  audio_url?: string | null;
  stored_audio_url?: string | null;
  downloads?: { video_url?: string };
}

interface UseGallerySelectionOptions {
  items: SelectableItem[];
  type: 'image' | 'video' | 'music' | 'speech';
  onDelete: (id: string) => Promise<void>;
  onRefresh?: () => void;
}

export function useGallerySelection({ items, type, onDelete, onRefresh }: UseGallerySelectionOptions) {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const { toast } = useToast();

  const toggleSelection = useCallback((id: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedItems(new Set(items.map(i => i.id)));
  }, [items]);

  const deselectAll = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  const cancelSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedItems(new Set());
  }, []);

  const startSelection = useCallback(() => {
    setSelectionMode(true);
  }, []);

  const getFileUrl = (item: SelectableItem): string | null => {
    if (type === 'image') return item.image_url || null;
    if (type === 'video') return item.downloads?.video_url || null;
    if (type === 'music' || type === 'speech') return item.stored_audio_url || item.audio_url || null;
    return null;
  };

  const getFileName = (item: SelectableItem, index: number): string => {
    const baseName = (item.prompt || item.title || item.text || `${type}_${index + 1}`)
      .slice(0, 30)
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();
    
    const ext = type === 'image' ? 'webp' : type === 'video' ? 'mp4' : 'mp3';
    return `${baseName}.${ext}`;
  };

  // State for bulk delete confirmation dialog
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  const confirmBulkDelete = useCallback(async () => {
    if (selectedItems.size === 0) return;

    setIsBulkDeleting(true);
    let deleted = 0;
    let failed = 0;

    for (const id of selectedItems) {
      try {
        await onDelete(id);
        deleted++;
      } catch (error) {
        console.error(`Failed to delete ${id}:`, error);
        failed++;
      }
    }

    setIsBulkDeleting(false);
    setSelectedItems(new Set());
    setSelectionMode(false);
    setShowBulkDeleteConfirm(false);

    if (failed === 0) {
      toast({
        title: 'Deleted',
        description: `Successfully deleted ${deleted} item${deleted > 1 ? 's' : ''}.`,
      });
    } else {
      toast({
        title: 'Partial Success',
        description: `Deleted ${deleted}, failed ${failed}.`,
        variant: 'destructive',
      });
    }

    onRefresh?.();
  }, [selectedItems, onDelete, onRefresh, toast]);

  const bulkDelete = useCallback(() => {
    if (selectedItems.size === 0) return;
    setShowBulkDeleteConfirm(true);
  }, [selectedItems]);

  const bulkDownload = useCallback(async () => {
    if (selectedItems.size === 0) return;

    const selectedList = items.filter(i => selectedItems.has(i.id));

    // Warn for large video bulk downloads (videos are ~50MB each)
    if (type === 'video' && selectedList.length > 10) {
      const confirmed = window.confirm(
        `Downloading ${selectedList.length} videos may take a while and use significant memory. Continue?`
      );
      if (!confirmed) return;
    }
    
    if (selectedList.length === 1) {
      // Single item - direct download
      const item = selectedList[0];
      const url = getFileUrl(item);
      if (!url) {
        toast({ title: 'Error', description: 'File not available for download.', variant: 'destructive' });
        return;
      }

      try {
        toast({ title: 'Downloading...', description: 'Please wait...' });
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = getFileName(item, 0);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
        toast({ title: 'Downloaded', description: 'File downloaded successfully.' });
      } catch (error) {
        console.error('Download error:', error);
        window.open(url, '_blank');
        toast({ title: 'Download', description: 'If download didn\'t start, file opened in new tab.' });
      }
      return;
    }

    // Multiple items - create ZIP
    setIsBulkDownloading(true);
    const zip = new JSZip();
    let downloaded = 0;
    let failed = 0;

    toast({
      title: 'Preparing download...',
      description: `Processing 0/${selectedList.length} files...`,
    });

    for (let i = 0; i < selectedList.length; i++) {
      const item = selectedList[i];
      const url = getFileUrl(item);
      
      if (!url) {
        failed++;
        continue;
      }

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Fetch failed');
        const blob = await response.blob();
        zip.file(getFileName(item, i), blob);
        downloaded++;
      } catch (error) {
        console.error(`Failed to download ${item.id}:`, error);
        failed++;
      }
    }

    if (downloaded === 0) {
      toast({ title: 'Error', description: 'No files could be downloaded.', variant: 'destructive' });
      setIsBulkDownloading(false);
      return;
    }

    try {
      toast({ title: 'Creating ZIP...', description: 'Almost done...' });
      const content = await zip.generateAsync({ type: 'blob' });
      const blobUrl = window.URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${type}s_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);

      toast({
        title: 'Download Complete',
        description: `ZIP created with ${downloaded} file${downloaded > 1 ? 's' : ''}${failed > 0 ? ` (${failed} failed)` : ''}.`,
      });
    } catch (error) {
      console.error('ZIP creation error:', error);
      toast({ title: 'Error', description: 'Failed to create ZIP file.', variant: 'destructive' });
    }

    setIsBulkDownloading(false);
    setSelectionMode(false);
    setSelectedItems(new Set());
  }, [selectedItems, items, type, toast]);

  return {
    selectedItems,
    selectionMode,
    isBulkDeleting,
    isBulkDownloading,
    toggleSelection,
    selectAll,
    deselectAll,
    cancelSelection,
    startSelection,
    bulkDelete,
    bulkDownload,
    // Bulk delete dialog control
    showBulkDeleteConfirm,
    setShowBulkDeleteConfirm,
    confirmBulkDelete,
    selectedCount: selectedItems.size,
  };
}